import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, filter, switchMap, take, Subject, map } from 'rxjs';
import { environment } from '@environments/environment';
import { CanalVenta } from '@core/models';
import {
  ResponseSuccess,
} from '@interfaces/response-success';
import { Store } from '@ngrx/store';
import { selectCredential, selectSaleChannelList } from '@store/selectors';
import { Credential } from '@core/models';
import { transformDropdown } from '@core/helpers';
import { MenuItem } from 'primeng/api';

@Injectable({ providedIn: 'root' })
export class SaleChannelService {
  credential$!: Observable<Credential | null>;
  saleChannelList$: Observable<CanalVenta[] | null>;

  private createCompletedSource = new Subject<void>();
  createCompleted$ = this.createCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private removeCompletedSource = new Subject<void>();
  removeCompleted$ = this.removeCompletedSource.asObservable();

  private closeDialogSource = new Subject<void>();
  closeDialog$ = this.closeDialogSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.saleChannelList$ = this.store.select(selectSaleChannelList);
  }

  createCompleted(): void {
    this.createCompletedSource.next();
    this.closeDialog();
  }

  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeDialog();
  }

  removeCompleted(): void {
    this.removeCompletedSource.next();
    this.closeDialog();
  }

  closeDialog(): void {
    this.closeDialogSource.next();
  }

  find(id: string): Observable<ResponseSuccess<CanalVenta>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<CanalVenta>>(
          `${environment.microConfiguraciones}/api/v1/canal-venta/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1)
    );
  }

  findAll(): Observable<ResponseSuccess<CanalVenta>> {
    return this.credential$.pipe(
      filter(
        (credential): credential is Credential =>
          !!credential && !!credential.granted
      ),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {};

        return this.http.get<ResponseSuccess<CanalVenta>>(
          `${environment.microConfiguraciones}/api/v1/canal-venta`,
          {
            headers: { Authorization: token },
            params: { ...params, nivel: 'licencia' },
          }
        );
      }),
      take(1)
    );
  }

  getDropdown(): Observable<MenuItem[]> {
    return this.saleChannelList$.pipe(
      filter((saleChannels): saleChannels is CanalVenta[] => !!saleChannels),
      map((saleChannels) => transformDropdown(saleChannels, 'idCanalVenta', 'nombre')),
    );
  }
}
